import {
  faDollarSign,
  faLocationDot,
  faTv,
  faUsers,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AspectRatio,
  Badge,
  Group,
  Image,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import { Location } from '../../models/Location'
import {
  COLORS,
  defaultImageUrl,
  HostOnboardingSteps,
} from '../../utils/constants'
import { formatNumber } from '../../utils/displayUtils'
import { getCurrentStepFromLocation } from '../../utils/locationUtils'
import classes from '../css/LocationCard.module.css'

const footTrafficLabels = {
  1: 'Low',
  2: 'Moderate',
  3: 'Steady',
  4: 'Busy',
  5: 'High',
}

const footTrafficMapping = {
  low: 1,
  moderate: 2,
  steady: 3,
  busy: 4,
  high: 5,
}

const LocationCard = ({ location }: LocationCardProps) => {
  const navigate = useNavigate()
  const visibleDevices = location.devices.filter((device) => device.isVisible)
  const device = visibleDevices[0]
  const isComplete =
    getCurrentStepFromLocation(location) === HostOnboardingSteps.Review

  const footTrafficLevel = location.footTraffic
    ? footTrafficMapping[
        location.footTraffic as keyof typeof footTrafficMapping
      ] || 0
    : 0
  const footTrafficLabel =
    footTrafficLevel > 0
      ? footTrafficLabels[footTrafficLevel as keyof typeof footTrafficLabels]
      : 'Not Set'

  return (
    <UnstyledButton
      onClick={() => {
        navigate(`/locations/${location.id}`)
      }}
    >
      <Stack>
        {location.imageUrls.length > 0 ? (
          <AspectRatio ratio={4 / 3}>
            <Image src={location.imageUrls[0]} style={{ borderRadius: 12 }} />
          </AspectRatio>
        ) : (
          <AspectRatio ratio={4 / 3}>
            <Image src={defaultImageUrl} style={{ borderRadius: 12 }} />
          </AspectRatio>
        )}
        <Group justify='space-between' align='center'>
          <Title
            size='h3'
            title={location.name}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {location.name}
          </Title>
          {!isComplete && (
            <Badge color={COLORS.HiddenGemzRed} size='sm'>
              Incomplete
            </Badge>
          )}
        </Group>
        <Stack gap={8}>
          <Group justify='space-between'>
            <Group gap={5} flex={1}>
              <FontAwesomeIcon icon={faTv} size='xs' width={15} />
              <Text className={classes.labelText}>Display</Text>
            </Group>
            <Text className={classes.detailText} flex={1}>
              {visibleDevices.length > 1
                ? `${visibleDevices.length} Devices`
                : !device?.screenSize
                ? `Not Configured`
                : `${device?.screenSize}" TV`}
            </Text>
          </Group>
          <Group justify='space-between'>
            <Group gap={5} flex={1}>
              <FontAwesomeIcon icon={faLocationDot} size='xs' width={15} />
              <Text className={classes.labelText} flex={1}>
                Display Location
              </Text>
            </Group>

            <Text className={classes.detailText} flex={1}>
              {device?.name || 'Not Configured'}
            </Text>
          </Group>
          <Group justify='space-between'>
            <Group gap={5} flex={1}>
              <FontAwesomeIcon icon={faDollarSign} size='xs' width={15} />
              <Text className={classes.labelText} flex={1}>
                Monthly Price
              </Text>
            </Group>

            <Text className={classes.detailText} flex={1}>
              {device?.monthlyPrice
                ? `$${formatNumber(device?.monthlyPrice)}/month`
                : 'Not Configured'}
            </Text>
          </Group>
          <Group justify='space-between'>
            <Group gap={5} flex={1}>
              <FontAwesomeIcon icon={faUsers} size='xs' width={15} />
              <Text className={classes.labelText} flex={1}>
                Foot Traffic
              </Text>
            </Group>
            <Text className={classes.detailText} flex={1}>
              {footTrafficLabel}
            </Text>
          </Group>
        </Stack>
      </Stack>
    </UnstyledButton>
  )
}

export default LocationCard

interface LocationCardProps {
  location: Location
}
